import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { useAdminOrders, useKitchenStations } from '../../lib/admin-hooks';
import type { ActionResult } from '../../lib/admin-hooks';
import { Skeleton } from '../../components/ui/Skeleton';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { CaprichoKDS } from '../../components/admin/CaprichoKDS';
import { MenuToggleDrawer } from '../../components/admin/MenuToggleDrawer';
import { VoidConfirmModal } from '../../components/admin/VoidConfirmModal';
import { logVoidAudit, updateVoidedColumns } from '../../lib/void-audit';
import { Clock, Check, AlertCircle, CreditCard, Banknote, Smartphone, Play, ChefHat, Truck, XCircle, X, Eye, Search, UtensilsCrossed } from 'lucide-react';
import { KDSOrderDetailModal } from '../../components/admin/KDSOrderDetailModal';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
// ─── Alert sound for completed orders ────────────────────────
const playCompletionSound = () => {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    // Play a pleasant two-tone chime
    const playTone = (freq: number, startTime: number, duration: number) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = audioCtx.currentTime;
    playTone(880, now, 0.15); // A5
    playTone(1108, now + 0.15, 0.15); // C#6
    playTone(1320, now + 0.3, 0.3); // E6
  } catch (e) {
    console.warn('[KDS] Could not play sound:', e);
  }
};
// ─── LOUD alert sound for new orders arriving in the kitchen ──
// Plays an attention-grabbing "ding-dong" alarm — 3 cycles of a
// high/low pair with a square wave for maximum audibility in a
// noisy kitchen environment.
const playNewOrderSound = () => {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const playTone = (freq: number, startTime: number, duration: number, volume = 0.55) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.frequency.value = freq;
      osc.type = 'square';
      gain.gain.setValueAtTime(volume, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = audioCtx.currentTime;
    // 3 cycles of "ding-dong" — loud and distinctive
    for (let i = 0; i < 3; i++) {
      const cycleStart = now + i * 0.5;
      playTone(1200, cycleStart, 0.2); // high "ding"
      playTone(800, cycleStart + 0.22, 0.25); // low "dong"
    }
  } catch (e) {
    console.warn('[KDS] Could not play new-order sound:', e);
  }
};
// Global roles that can manage the full order lifecycle
const GLOBAL_ROLES = ['owner', 'admin', 'manager', 'caja', 'super_admin', 'platform_admin'];
interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}
export function KDSPage() {
  const slug = useTenantSlug();
  const [searchParams] = useSearchParams();
  const lockedStation = searchParams.get('station');
  const {
    restaurant,
    settings,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const [kitchenPaused, setKitchenPaused] = useState(false);
  const [togglingKitchen, setTogglingKitchen] = useState(false);
  useEffect(() => {
    if (settings) {
      console.log('[NOIZA][KDS] Settings loaded, kitchen_paused =', settings.kitchen_paused, 'restaurant_id =', restaurant?.id);
      setKitchenPaused(settings.kitchen_paused);
    }
  }, [settings, restaurant?.id]);
  const handleToggleKitchen = async () => {
    if (!restaurant) {
      console.error('[NOIZA][KDS] No restaurant loaded — cannot toggle');
      addToast('error', 'No se pudo cargar el restaurante');
      return;
    }
    setTogglingKitchen(true);
    const newValue = !kitchenPaused;
    console.log('[NOIZA][KDS] Toggling kitchen:', {
      restaurantId: restaurant.id,
      slug,
      currentValue: kitchenPaused,
      newValue
    });
    const {
      toggleKitchenPause
    } = await import('../../lib/kitchen-toggle');
    const result = await toggleKitchenPause(restaurant.id, newValue);
    if (result.success) {
      setKitchenPaused(result.newValue);
      addToast('success', result.newValue ? '⏸ Cocina pausada — no se reciben pedidos' : '▶ Cocina activa — recibiendo pedidos');
    } else {
      setKitchenPaused(result.newValue);
      addToast('error', result.error || 'No se pudo actualizar el estado de cocina');
      console.error('[NOIZA][KDS] Toggle FAILED:', result.error);
    }
    setTogglingKitchen(false);
  };
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const {
    stations,
    loading: stationsLoading
  } = useKitchenStations(restaurant?.id);
  const {
    orders,
    loading: ordersLoading,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder,
    markItemReady,
    markStationItemsReady,
    refetchOrders,
    setOrders
  } = useAdminOrders(restaurant?.id);
  const [activeStation, setActiveStation] = useState<string>('all');
  const [updatingOrders, setUpdatingOrders] = useState<Set<string>>(new Set());
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [isMenuDrawerOpen, setIsMenuDrawerOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [detailOrder, setDetailOrder] = useState<any>(null);
  const [deliveryJobsData, setDeliveryJobsData] = useState<Record<string, any>>({});
  const [voidModalState, setVoidModalState] = useState<{
    isOpen: boolean;
    orderId: string | null;
    originalAmount: number;
  }>({
    isOpen: false,
    orderId: null,
    originalAmount: 0
  });
  const completedOrdersRef = useRef<Set<string>>(new Set());
  // Track order IDs we've already announced with the new-order alert sound.
  // Initialized on first load to avoid blasting the sound for every order
  // that was already on screen when the KDS opened.
  const seenOrdersRef = useRef<Set<string>>(new Set());
  const seenOrdersInitializedRef = useRef(false);
  const isCapricho = String(restaurant?.id) === '2' || restaurant?.slug === 'capricho-de-canela';
  const isNoiza = String(restaurant?.id) === '1' || restaurant?.slug === 'noiza';
  // La Cabaña Parrillera (id 24) — orders skip the manual "Aceptar" gate
  // and appear in the production stations as soon as they arrive.
  const isCabanaParrillera = String(restaurant?.id) === '24' || restaurant?.slug === 'la-cabana-parrillera' || restaurant?.slug === 'cabana-parrillera';
  // Emilius Pizza (id 27) — same KDS behavior as La Cabaña Parrillera:
  // orders bypass the manual "Aceptar" gate and show up directly in the
  // production stations (Cocina / Pizzería / Bebidas). Unlike Cabaña,
  // Emilius KEEPS the "Pizzería" station available.
  const isEmilius = String(restaurant?.id) === '27' || restaurant?.slug === 'emilius-pizza';
  // Rancho San Francisco (id 28) — same KDS behavior as La Cabaña
  // Parrillera: orders bypass the manual "Aceptar" gate and show up
  // directly in the production stations (Cocina / Bebidas).
  const isRanchoSanFrancisco = String(restaurant?.id) === '28' || restaurant?.slug === 'rancho-san-francisco';
  // NatuCafé Coffee Shop (id 19) — same KDS behavior as La Cabaña /
  // Rancho / Emilius: las órdenes desde "Tomar Orden" entran directo a
  // la estación de producción (Cocina / Postres y Batidos). No hay
  // paso intermedio de "Aceptar" en caja porque el mesero ya tomó la
  // orden. Las órdenes del marketplace siguen el flujo normal (caja
  // las acepta) — esto NO las afecta.
  const isNatuCafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop' || restaurant?.slug?.toLowerCase().includes('natucafe');
  // NatuCafé — vista Cocina específicamente. Cuando el filtro activo
  // es la estación 'cocina' agrandamos TODO el texto de la tarjeta del
  // KDS (orden#, mesa, items, botones) para que un cocinero con
  // discapacidad visual pueda leer las comandas desde lejos. Solo
  // aplica a la combinación NatuCafé + estación cocina — el resto de
  // restaurantes y el resto de las estaciones de NatuCafé mantienen
  // el layout compacto original.
  const isNatuCafeCocinaView = isNatuCafe && activeStation === 'cocina';
  // ─── Fetch Delivery Jobs for active orders ───────────────────
  useEffect(() => {
    if (!orders || orders.length === 0) return;
    const fetchDeliveryJobs = async () => {
      const deliveryOrderIds = orders.filter((o: any) => o.delivery_type === 'delivery' && ['searching_driver', 'ready', 'out_for_delivery'].includes(o.status)).map((o: any) => o.id);
      if (deliveryOrderIds.length === 0) return;
      try {
        const {
          data: jobs
        } = await supabase.from('delivery_jobs').select('id, order_id, status, driver_id, created_at').in('order_id', deliveryOrderIds);
        if (!jobs) return;
        const driverIds = jobs.filter(j => j.driver_id).map(j => j.driver_id);
        let driversMap: Record<number, string> = {};
        if (driverIds.length > 0) {
          const {
            data: drivers
          } = await supabase.from('drivers').select('id, full_name').in('id', driverIds);
          if (drivers) {
            drivers.forEach(d => {
              driversMap[d.id] = d.full_name;
            });
          }
        }
        const newJobsData: Record<string, any> = {};
        jobs.forEach(job => {
          const searchingMinutes = Math.floor((new Date().getTime() - new Date(job.created_at).getTime()) / 60000);
          newJobsData[job.order_id] = {
            ...job,
            driverName: job.driver_id ? driversMap[job.driver_id] : null,
            searchingMinutes: searchingMinutes > 0 ? searchingMinutes : 0
          };
        });
        setDeliveryJobsData(newJobsData);
      } catch (err) {
        console.error('[KDS] Error fetching delivery jobs:', err);
      }
    };
    fetchDeliveryJobs();
    const interval = setInterval(fetchDeliveryJobs, 10000);
    return () => clearInterval(interval);
  }, [orders]);
  // ─── Rancho San Francisco (id 28) — Auto-refresh del KDS cada 5s ───
  // El KDS del Rancho corre en un POS sin teclado. Para que el dueño
  // vea pedidos nuevos del marketplace o cambios sin tener que tocar
  // refrescar, refetcheamos las órdenes cada 5 segundos. Solo aplica
  // a Rancho — no toca a ningún otro restaurante.
  useEffect(() => {
    if (!isRanchoSanFrancisco) return;
    if (!restaurant?.id) return;
    const interval = setInterval(() => {
      refetchOrders();
    }, 5000);
    return () => clearInterval(interval);
  }, [isRanchoSanFrancisco, restaurant?.id, refetchOrders]);
  // ─── Rancho San Francisco (id 28) — Estado para botón "Imprimir" ───
  // Inserta una fila en order_print_jobs con un trigger único
  // (manual_<timestamp>) para que el unique index de dedupe NO bloquee
  // reimpresiones. El bridge Python detecta el pending y manda el ticket
  // a la impresora predeterminada del POS. NO toca el flujo automático.
  const [printingOrders, setPrintingOrders] = useState<Set<string>>(new Set());
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  const userRole = tenantCtx?.role || 'owner';
  const isGlobalRole = GLOBAL_ROLES.includes(userRole);
  const isGlobalStation = (code: string) => code === 'all' || code === 'caja';
  const isLockedToProduction = lockedStation != null && !isGlobalStation(lockedStation);
  useEffect(() => {
    if (lockedStation) {
      const resolved = lockedStation === 'caja' ? 'all' : lockedStation;
      console.log('[NOIZA][KDS] === STATION DEBUG ===');
      console.log('[NOIZA][KDS] URL station param:', lockedStation);
      console.log('[NOIZA][KDS] Resolved activeStation:', resolved);
      console.log('[NOIZA][KDS] isGlobalStation:', isGlobalStation(resolved));
      console.log('[NOIZA][KDS] isLockedToProduction:', !isGlobalStation(lockedStation));
      setActiveStation(resolved);
    }
  }, [lockedStation]);
  // ─── Detect when ALL items in an order become ready → play sound ───
  // Must be before conditional returns to respect Rules of Hooks
  const activeOrdersForSound = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status));
  useEffect(() => {
    if (!orders.length) return;
    const newlyCompleted: string[] = [];
    for (const order of activeOrdersForSound) {
      if (order.status !== 'preparing') continue;
      // Ignore retail/Estantería items when deciding if the kitchen
      // portion of the order is done — they don't belong to cocina.
      const kitchenItems = order.items.filter((i: any) => i.product_type !== 'retail');
      const allReady = kitchenItems.length > 0 && kitchenItems.every((i: any) => i.status === 'ready' || i.status === 'served');
      if (allReady && !completedOrdersRef.current.has(order.id)) {
        newlyCompleted.push(order.id);
      }
    }
    if (newlyCompleted.length > 0) {
      newlyCompleted.forEach(id => completedOrdersRef.current.add(id));
      playCompletionSound();
      newlyCompleted.forEach(id => {
        const o = activeOrdersForSound.find((ord: any) => ord.id === id);
        if (o) {
          const parts = o.order_number.split('-');
          const short = parts[parts.length - 1] || o.order_number;
          addToast('success', `🎉 Pedido #${short} completado`);
        }
      });
      console.log('[KDS] 🔔 All items ready for orders:', newlyCompleted);
    }
  }, [orders]);
  // ─── Detect newly arrived orders → play LOUD alert sound ────
  // Fires whenever an order id appears in the active list that
  // wasn't there before. Skips pos_quick_sale (Estantería) and
  // delivered/cancelled. First render seeds the set silently so
  // existing orders don't trigger a flood of alerts on mount.
  useEffect(() => {
    if (!orders.length) {
      // Mark initialized even if empty so the first real order triggers sound
      seenOrdersInitializedRef.current = true;
      return;
    }
    const currentActive = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status) && o.source !== 'pos_quick_sale');
    if (!seenOrdersInitializedRef.current) {
      // First load — seed without playing sound
      currentActive.forEach((o: any) => seenOrdersRef.current.add(o.id));
      seenOrdersInitializedRef.current = true;
      return;
    }
    const newlyArrived = currentActive.filter((o: any) => !seenOrdersRef.current.has(o.id));
    if (newlyArrived.length > 0) {
      newlyArrived.forEach((o: any) => seenOrdersRef.current.add(o.id));
      playNewOrderSound();
      newlyArrived.forEach((o: any) => {
        const parts = o.order_number.split('-');
        const short = parts[parts.length - 1] || o.order_number;
        addToast('success', `🔔 Nuevo pedido #${short}`);
      });
      console.log('[KDS] 🔔 New orders arrived:', newlyArrived.map((o: any) => o.order_number));
    }
  }, [orders, addToast]);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-4706">
        <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-4707" />
      </div>;
  }
  if (isCapricho) {
    return <AdminLayout restaurant={restaurant} role={userRole} onSignOut={tenantCtx?.signOut} data-id="element-4708">
        <CaprichoKDS restaurant={restaurant} data-id="element-4709" />
      </AdminLayout>;
  }
  // Show active orders (not delivered/cancelled).
  // Estantería / POS de venta rápida (source='pos_quick_sale') NUNCA
  // debe verse en el KDS — son productos de retail (canastos, café
  // empacado, etc.) que se entregan en caja, no requieren preparación
  // en cocina. Su factura/recibo se imprime por separado. Filtrar acá
  // garantiza que tampoco aparezcan en "Todos" ni en ninguna estación.
  //
  // ── IMPORTANTE: items retail (product_type='retail') solo se ocultan
  // en vistas de estación (cocina, bebidas, pizzeria, etc.). En la vista
  // global "Todos" / "Caja" SÍ se muestran — caja los necesita ver para
  // cobrarlos. La factura/POS de caja vive en otras pantallas
  // (PayTableModal, CajaOverviewPage) que no pasan por este filtro.
  const stripRetailItems = !isGlobalStation(activeStation);
  const activeOrders = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status) && o.source !== 'pos_quick_sale').map((o: any) => {
    let items: any[] = o.items || [];
    // ── ESTANTERÍA (solo NatuCafé id 19) ──────────────────────
    // Los productos de estantería son retail (product_type='retail').
    // NUNCA aparecen en el KDS — ni en "Todos"/"Caja" ni en ninguna
    // estación de cocina/bebidas. Son venta de retail que se cobra
    // y factura aparte; no requieren preparación. Su station_code es
    // NULL, por eso identificamos por product_type (la fuente de
    // verdad), no por station_code. Scope estricto a id 19: el resto
    // de restaurantes no se ve afectado. El cobro, los pagos y la
    // factura impresa NO pasan por este filtro.
    if (isNatuCafe) {
      items = items.filter((it: any) => it.product_type !== 'retail');
    }
    // Retail (product_type='retail') también se oculta en vistas de
    // estación para el resto de restaurantes.
    if (stripRetailItems) {
      items = items.filter((it: any) => it.product_type !== 'retail');
    }
    return items === o.items ? o : {
      ...o,
      items
    };
  }).filter((o: any) => (o.items || []).length > 0);
  // DEBUG: Log station filtering details (comprehensive)
  if (!isGlobalStation(activeStation) && activeOrders.length > 0) {
    console.log('[NOIZA][KDS] ═══════════════════════════════════');
    console.log('[NOIZA][KDS] STATION FILTER DEBUG');
    console.log('[NOIZA][KDS] activeStation:', activeStation);
    console.log('[NOIZA][KDS] Total active orders:', activeOrders.length);
    activeOrders.forEach((order: any) => {
      const matchingItems = order.items.filter((i: any) => i.station_code === activeStation);
      console.log(`[NOIZA][KDS] Order #${order.order_number} (id: ${order.id}, status: ${order.status})`);
      order.items.forEach((i: any) => {
        const match = i.station_code === activeStation ? '✅' : '❌';
        console.log(`  ${match} ${i.item_name_snapshot} — station_code: "${i.station_code}" (status: ${i.status})`);
      });
      console.log(`  → ${matchingItems.length}/${order.items.length} items match "${activeStation}"`);
    });
    console.log('[NOIZA][KDS] ═══════════════════════════════════');
  }
  const displayOrders = activeOrders.filter((order: any) => {
    if (isGlobalStation(activeStation)) return true;
    // Station views (cocina, pizzeria, bebidas) only see orders that have been
    // accepted by caja — pending/new orders are hidden from production stations.
    // EXCEPTION: La Cabaña Parrillera, Emilius Pizza and Rancho San
    // Francisco show new orders directly in stations (no manual
    // acceptance step required).
    if (order.status === 'new' && !isCabanaParrillera && !isEmilius && !isRanchoSanFrancisco && !isNatuCafe) return false;
    // In station view, check if there are any NON-ready items for this station
    const stationItems = order.items.filter((item: any) => item.station_code === activeStation);
    const pendingStationItems = stationItems.filter((item: any) => item.status !== 'ready' && item.status !== 'served' && item.status !== 'cancelled');
    // Hide order from station if all its items for this station are done
    // (ready/served) or cancelled (removed by cashier from Modificar/Cancelar).
    return pendingStationItems.length > 0;
  });
  // DEBUG: Log final display result
  if (!isGlobalStation(activeStation)) {
    console.log('[NOIZA][KDS] displayOrders for station', activeStation, ':', displayOrders.length);
  }
  const getWaitTimeColor = (createdAt: string) => {
    const minutes = Math.floor((new Date().getTime() - new Date(createdAt).getTime()) / 60000);
    if (minutes > 20) return 'text-red-500 bg-red-50 border-red-200';
    if (minutes > 10) return 'text-amber-500 bg-amber-50 border-amber-200';
    return 'text-emerald-500 bg-emerald-50 border-emerald-200';
  };
  // ─── Order-level action handler ────────────────────────────
  const handleOrderAction = async (orderId: string, action: string) => {
    // DEBUG: Verify correct ID usage
    const targetOrder = orders.find((o: any) => o.id === orderId);
    const shortDisplay = targetOrder ? getShortOrderNumber(targetOrder.order_number) : '?';
    console.log('[NOIZA][KDS] === ORDER ACTION DEBUG ===');
    console.log('[NOIZA][KDS] order.id (real PK):', orderId);
    console.log('[NOIZA][KDS] order.order_number:', targetOrder?.order_number);
    console.log('[NOIZA][KDS] short display #:', shortDisplay);
    console.log('[NOIZA][KDS] action:', action);
    console.log('[NOIZA][KDS] update payload will use .eq("id",', orderId, ')');
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    let result: ActionResult = {
      success: false,
      error: 'Acción desconocida'
    };
    try {
      switch (action) {
        case 'accept':
          result = await acceptOrder(orderId);
          if (result.success) addToast('success', '✓ Pedido aceptado');
          break;
        case 'start_preparation':
          result = await startOrderPreparation(orderId);
          if (result.success) addToast('success', '🔥 Enviado a cocina');
          break;
        case 'mark_ready':
          result = await markOrderReady(orderId);
          if (result.success) addToast('success', '✅ Pedido listo');
          break;
        case 'deliver':
          result = await deliverOrder(orderId);
          if (result.success) addToast('success', '🚀 Entregado');
          break;
        case 'cancel':
          const orderToCancel = orders.find((o: any) => o.id === orderId);
          setVoidModalState({
            isOpen: true,
            orderId,
            originalAmount: orderToCancel?.total || 0
          });
          // Return early, actual cancellation happens in handleVoidConfirm
          setUpdatingOrders(prev => {
            const next = new Set(prev);
            next.delete(orderId);
            return next;
          });
          return;
      }
      if (!result.success && result.error) {
        addToast('error', result.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error inesperado');
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  const handleVoidConfirm = async (authorizedBy: string, reason: string) => {
    if (!voidModalState.orderId || !restaurant?.id) return;
    const orderId = voidModalState.orderId;
    setVoidModalState({
      isOpen: false,
      orderId: null,
      originalAmount: 0
    });
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    try {
      await updateVoidedColumns('orders', Number(orderId), authorizedBy, reason);
      await logVoidAudit({
        restaurant_id: restaurant.id,
        order_id: Number(orderId),
        action_type: 'cancel_order',
        authorized_by: authorizedBy,
        reason,
        original_amount: voidModalState.originalAmount
      });
      const result = await cancelOrder(orderId);
      if (result.success) {
        addToast('success', '❌ Pedido anulado correctamente');
      } else if (result.error) {
        addToast('error', result.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error al anular pedido');
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  // ─── Mark single item ready (with RPC + fallback) ────────────
  const markItemReadyWithFallback = async (orderItemId: number, rpcName: string) => {
    console.log(`🔥 KDS item clicked (${rpcName})`, orderItemId);
    const itemIdStr = String(orderItemId);
    setUpdatingItems(prev => new Set(prev).add(itemIdStr));
    // Optimistic update — immediately mark item as ready in local state
    setOrders((prev: any[]) => prev.map((order: any) => ({
      ...order,
      items: order.items.map((item: any) => String(item.id) === itemIdStr ? {
        ...item,
        status: 'ready'
      } : item)
    })));
    try {
      // Step 1: Try RPC
      const {
        data,
        error
      } = await supabase.rpc(rpcName, {
        p_order_item_id: orderItemId
      });
      let updated = false;
      if (error) {
        console.warn(`🔥 RPC ${rpcName} failed:`, error.message, '— using direct update fallback');
      } else {
        updated = true;
      }
      // Step 2: Fallback — direct update if RPC failed
      if (!updated) {
        console.log('🔥 Fallback: direct order_items update for id:', orderItemId);
        const {
          error: updateError,
          count
        } = await supabase.from('order_items').update({
          status: 'ready'
        }).eq('id', orderItemId);
        if (updateError) {
          console.error('🔥 Fallback update also failed:', updateError);
          addToast('error', `Error: ${updateError.message}`);
          // Revert optimistic update
          await refetchOrders();
          return;
        }
        console.log('🔥 Fallback update succeeded');
      }
      // Step 3: Refetch and verify
      await refetchOrders();
      addToast('success', '✅ Item listo');
    } catch (err: any) {
      console.error('🔥 EXCEPTION:', err);
      addToast('error', err.message);
      // Revert optimistic update on error
      await refetchOrders();
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemIdStr);
        return next;
      });
    }
  };
  const handleNoizaMarkReady = (orderItemId: number) => markItemReadyWithFallback(orderItemId, 'noiza_kds_mark_item_ready');
  const handleMarkReady = (orderItemId: number) => markItemReadyWithFallback(orderItemId, 'kds_mark_item_ready');
  // ─── Station-level action handler ──────────────────────────
  const handleStationAction = async (orderId: string, stationCode: string) => {
    console.log('[NOIZA][KDS] === STATION ACTION DEBUG ===');
    console.log('[NOIZA][KDS] order.id:', orderId);
    console.log('[NOIZA][KDS] station_code:', stationCode);
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    // Optimistic update — immediately mark all station items as ready
    const isAllStations = stationCode === 'all' || stationCode === 'caja';
    setOrders((prev: any[]) => prev.map((order: any) => {
      if (order.id !== orderId) return order;
      return {
        ...order,
        items: order.items.map((item: any) => {
          if (item.status === 'ready' || item.status === 'served') return item;
          if (isAllStations || item.station_code === stationCode) {
            return {
              ...item,
              status: 'ready'
            };
          }
          return item;
        })
      };
    }));
    try {
      const result = await markStationItemsReady(orderId, stationCode);
      if (result.success) {
        addToast('success', '✅ Items de estación marcados listos');
      } else if (result.error) {
        addToast('error', result.error);
        // Revert optimistic update on failure
        await refetchOrders();
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error al marcar items listos');
      await refetchOrders();
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  // ─── Rancho San Francisco (id 28) — Manual print handler ───────
  // Inserta una fila pending en order_print_jobs con un trigger único
  // (manual_<timestamp>) para evitar el unique index de dedupe.
  // El bridge Python detecta el pending y manda el ticket a la
  // impresora predeterminada del POS. Solo aplica a Rancho.
  const handleManualPrint = async (order: any) => {
    if (!isRanchoSanFrancisco) return;
    if (!order?.id) return;
    setPrintingOrders(prev => new Set(prev).add(order.id));
    try {
      const triggerValue = `manual_${Date.now()}`;
      // order_print_jobs.order_id es UUID — usamos tracking_token (UUID),
      // mismo patrón que WaiterPOSPage. order.id (PK) NO va al campo
      // order_id; lo guardamos en payload.order_internal_id por si el
      // bridge lo necesita.
      const orderRefUuid = (order as any).tracking_token ?? (order as any).id ?? null;
      console.log('[RANCHO_PRINT][manual] Inserting print job — order.id:', order.id, 'tracking_token:', (order as any).tracking_token, 'using ref:', orderRefUuid, 'trigger:', triggerValue);
      if (!orderRefUuid) {
        console.error('[RANCHO_PRINT][manual] No tracking_token / id available on order', order);
        addToast('error', 'Orden sin identificador (no se puede imprimir)');
        return;
      }
      const {
        error
      } = await supabase.from('order_print_jobs').insert({
        restaurant_id: 28,
        order_id: orderRefUuid,
        order_number: order.order_number ?? null,
        status: 'pending',
        payload: {
          order,
          order_internal_id: order.id ?? null,
          order_number: order.order_number ?? null,
          created_at: order.created_at ?? null,
          customer_name: order.customer_name ?? null,
          customer_phone: order.customer_phone ?? null,
          delivery_type: order.delivery_type ?? null,
          payment_method: order.payment_method ?? null,
          delivery_address: order.delivery_address ?? null,
          table_number: (order as any).table_number ?? null,
          subtotal: order.subtotal ?? 0,
          delivery_fee: order.delivery_fee ?? 0,
          total: order.total ?? 0,
          notes: order.notes ?? null,
          items: order.items ?? [],
          trigger: triggerValue
        }
      });
      if (error) {
        console.error('[RANCHO_PRINT][manual] insert error — full object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
        addToast('error', `No se pudo imprimir: ${error.message || 'error desconocido'}`);
      } else {
        console.log('[RANCHO_PRINT][manual] ✅ print job inserted');
        addToast('success', '🖨 Enviado a la impresora');
      }
    } catch (e: any) {
      console.error('[RANCHO_PRINT][manual] exception:', e);
      addToast('error', e?.message || 'Error al enviar a la impresora');
    } finally {
      setPrintingOrders(prev => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    }
  };
  // Header title
  let headerTitle = 'KDS — Todas las Estaciones';
  if (activeStation !== 'all') {
    if (isGlobalStation(activeStation)) {
      headerTitle = 'KDS — Caja (Vista Global)';
    } else {
      const stationName = stations.find(s => s.code === activeStation)?.name;
      if (stationName) headerTitle = `KDS — ${stationName}`;
    }
  }
  const getPaymentIcon = (method: string) => {
    switch (method) {
      case 'sinpe':
        return <Smartphone className="w-4 h-4" data-id="element-4710" />;
      case 'card':
        return <CreditCard className="w-4 h-4" data-id="element-4711" />;
      case 'cash':
        return <Banknote className="w-4 h-4" data-id="element-4712" />;
      default:
        return null;
    }
  };
  const getPaymentLabel = (method: string) => {
    switch (method) {
      case 'sinpe':
        return 'SINPE';
      case 'card':
        return 'Tarjeta';
      case 'cash':
        return 'Efectivo';
      default:
        return method;
    }
  };
  const getOrderStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="info" className="bg-blue-50 text-blue-700 border-blue-200" data-id="element-4713">
            Nuevo
          </Badge>;
      case 'accepted':
        return <Badge variant="default" className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-4714">
            Aceptado
          </Badge>;
      case 'preparing':
        return <Badge variant="default" className="bg-amber-50 text-amber-700 border-amber-200" data-id="element-4715">
            Preparando
          </Badge>;
      case 'ready':
        return <Badge variant="success" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-id="element-4716">
            Listo
          </Badge>;
      case 'searching_driver':
        return <Badge variant="default" className="bg-orange-50 text-orange-700 border-orange-200 animate-pulse" data-id="element-4717">
            🔍 Buscando conductor
          </Badge>;
      case 'out_for_delivery':
        return <Badge variant="default" className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-4718">
            🚗 En camino
          </Badge>;
      case 'delivered':
        return <Badge variant="default" className="bg-gray-50 text-gray-700 border-gray-200" data-id="element-4719">
            ✅ Entregado
          </Badge>;
      default:
        return <Badge variant="default" data-id="element-4720">{status}</Badge>;
    }
  };
  const getItemStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'Pendiente',
          color: 'bg-gray-100 text-gray-600',
          actionLabel: 'En preparación',
          actionColor: 'bg-amber-500 hover:bg-amber-600 text-white border-amber-600'
        };
      case 'preparing':
        return {
          label: 'Preparando',
          color: 'bg-amber-100 text-amber-700',
          actionLabel: 'Marcar listo',
          actionColor: 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-600'
        };
      case 'ready':
        return {
          label: 'Listo',
          color: 'bg-emerald-100 text-emerald-700',
          actionLabel: '',
          actionColor: ''
        };
      default:
        return {
          label: status,
          color: 'bg-gray-100 text-gray-600',
          actionLabel: '',
          actionColor: ''
        };
    }
  };
  // Helper to format short order number
  const getShortOrderNumber = (orderNumber: string) => {
    const parts = orderNumber.split('-');
    return parts[parts.length - 1] || orderNumber;
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-4721">
      <div className="min-h-screen bg-[#0B0B0B] p-2 sm:p-3 text-white" data-id="element-4722">
        {/* Header — ultra compact */}
        <div className="flex items-center justify-between gap-3 mb-3" data-id="element-4723">
          <div className="flex items-center gap-2" data-id="element-4724">
            <h1 className="text-lg font-black text-white tracking-tight" data-id="element-4725">
              {headerTitle}
            </h1>
            <span className="bg-white/10 text-white/60 px-2 py-0.5 rounded-full text-xs font-bold tabular-nums" data-id="element-4726">
              {displayOrders.length}
            </span>
          </div>

          <div className="flex items-center gap-2" data-id="element-4727">
            {/* Menu Toggle Button */}
            {isGlobalRole && <button onClick={() => setIsMenuDrawerOpen(true)} className="h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-all active:scale-95 flex items-center gap-1.5 flex-shrink-0 bg-white/10 text-white hover:bg-white/20 border border-white/5" data-id="element-4728">
                <UtensilsCrossed className="w-3 h-3" data-id="element-4729" />
                Menú
              </button>}

            {/* Kitchen Pause Toggle */}
            {isGlobalRole && <button onClick={handleToggleKitchen} disabled={togglingKitchen} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-all active:scale-95 flex items-center gap-1.5 flex-shrink-0', kitchenPaused ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30', togglingKitchen && 'opacity-50 pointer-events-none')} data-id="element-4730">
                {kitchenPaused ? <>
                    <Play className="w-3 h-3" data-id="element-4731" />
                    Activar Cocina
                  </> : <>
                    <XCircle className="w-3 h-3" data-id="element-4732" />
                    Pausar Cocina
                  </>}
              </button>}
          </div>
        </div>

        {/* Station Tabs — compact pills.
            Para Rancho San Francisco (id 28) escondemos los pills por
            completo: el usuario sólo opera el KDS unificado ("Todos"),
            no hay tabs por estación. Mantenemos activeStation = 'all'
            de forma natural (es el default).
            NatuCafé (id 19) — los pills se renderizan ~3x más grandes
            (texto + padding + altura) porque el equipo opera el KDS
            desde lejos. Solo afecta este restaurante. */}
        {!isLockedToProduction && !(String(restaurant?.id) === '28' || restaurant?.slug === 'rancho-san-francisco') && <div className={cn('flex overflow-x-auto no-scrollbar', isNatuCafe ? 'gap-3' : 'gap-1.5')} data-id="element-4733">
              <button onClick={() => setActiveStation('all')} className={cn('rounded-full font-bold whitespace-nowrap transition-colors active:scale-95', isNatuCafe ? 'h-20 px-8 text-3xl rounded-2xl' : 'h-8 px-3 text-xs', activeStation === 'all' ? 'bg-white text-black' : 'bg-white/5 text-white/50 hover:bg-white/10')} data-id="element-4734">
                Todos
              </button>
              {stations.filter(station => {
          // NatuCafé (id 19) — hide "Pizzería" and "Barra" pills
          // from the KDS station tabs. They still exist in the DB
          // (so any item already assigned to those station_codes
          // keeps flowing through the system) and remain visible
          // under the "Todos" tab, but the kitchen doesn't see
          // useless filter buttons in this view. SCOPED strictly
          // to NatuCafé — every other restaurant keeps its full
          // station list intact (e.g. Emilius still shows
          // "Pizzería").
          if (!isNatuCafe) return true;
          const name = (station.name || '').toLowerCase().trim();
          const code = (station.code || '').toLowerCase().trim();
          const hidden = ['pizzeria', 'pizzería', 'barra'];
          return !hidden.includes(name) && !hidden.includes(code);
        }).map(station => <button key={station.id} onClick={() => setActiveStation(station.code)} className={cn('rounded-full font-bold whitespace-nowrap transition-colors active:scale-95', isNatuCafe ? 'h-20 px-8 text-3xl rounded-2xl' : 'h-8 px-3 text-xs', activeStation === station.code ? 'bg-white text-black' : 'bg-white/5 text-white/50 hover:bg-white/10')} data-id="element-4735">
                    {station.name}
                  </button>)}
            </div>}
        {/* Orders Grid — high density: 2→3→4 columns.
            NatuCafé Cocina: 3 tarjetas en pantalla a la vez, texto
            grande pero balanceado para que se vean MUCHOS productos
            por ticket sin scroll. Otras vistas mantienen 5 columnas. */}
        <div className={cn('grid gap-2 items-start', isNatuCafeCocinaView ? 'grid-cols-2 xl:grid-cols-3' : 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5')} data-id="element-4736">
          {ordersLoading || stationsLoading ? [1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-48 rounded-xl bg-[#141414] animate-pulse border border-white/5" data-id="element-4737" />) : displayOrders.length > 0 ? displayOrders.map((order: any) => {
          // Items that belong to this station (or all if global)
          const stationItems = order.items.filter((item: any) => isGlobalStation(activeStation) || item.station_code === activeStation);
          // In station view, only show non-ready items.
          // Also hide cancelled items so the kitchen never prepares
          // something the cashier already removed from the order.
          const visibleItems = isGlobalStation(activeStation) ? stationItems.filter((item: any) => item.status !== 'cancelled') : stationItems.filter((item: any) => item.status !== 'ready' && item.status !== 'served' && item.status !== 'cancelled');
          // DEBUG: Log filtering for each order
          if (!isGlobalStation(activeStation)) {
            console.log(`[NOIZA][KDS] Order #${order.order_number}:`);
            console.log(`  Total items: ${order.items.length}`);
            console.log(`  Station items for ${activeStation}: ${stationItems.length}`);
            console.log(`  Visible (pending) items: ${visibleItems.length}`);
          }
          // CRITICAL: Skip rendering if no pending items for this station
          if (visibleItems.length === 0) {
            return null;
          }
          const readyItemsCount = visibleItems.filter((i: any) => i.status === 'ready' || i.status === 'served').length;
          const allItemsReady = visibleItems.length > 0 && readyItemsCount === visibleItems.length;
          // "FALTAN..." only when there's partial preparation:
          // at least one item is ready but not all are ready
          const isPartiallyReady = readyItemsCount > 0 && !allItemsReady;
          const isUpdatingOrder = updatingOrders.has(order.id);
          const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
          let borderColor = 'border-white/10';
          let cardGlow = '';
          if (order.status === 'new') {
            borderColor = 'border-blue-500';
            cardGlow = 'kds-new-order';
          } else if (order.status === 'accepted') {
            borderColor = 'border-purple-500';
          } else if (order.status === 'preparing') {
            borderColor = 'border-amber-500';
          } else if (order.status === 'searching_driver') {
            borderColor = 'border-orange-500';
          } else if (order.status === 'out_for_delivery') {
            borderColor = 'border-cyan-500';
          } else if (allItemsReady) {
            borderColor = 'border-emerald-500';
          }
          return <div key={order.id} className={cn('bg-[#141414] rounded-xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden', borderColor, cardGlow)} data-id="element-4738">
                  {/* Header — single dense row */}
                  <div className="px-2.5 py-2 border-b border-white/5 flex items-center justify-between gap-1" data-id="element-4739">
                    <div className="flex items-center gap-1.5 min-w-0" data-id="element-4740">
                      <span className={cn('font-black text-white tabular-nums', isNatuCafeCocinaView ? 'text-2xl' : 'text-sm')} data-id="element-4741">
                        #{getShortOrderNumber(order.order_number)}
                      </span>
                      {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded text-[10px] font-bold" data-id="element-4742">
                          🛵
                        </span> : order.delivery_type === 'pickup' ? <span className="bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded text-[10px] font-bold" data-id="element-4743">
                          🏠
                        </span> : <span className="bg-white/10 text-white/50 px-1.5 py-0.5 rounded text-[10px] font-bold" data-id="element-4744">
                          🍽️{order.table_id || ''}
                        </span>}
                      <span className="text-white/30" data-id="element-4745">
                        {getPaymentIcon(order.payment_method)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0" data-id="element-4746">
                      <button onClick={e => {
                  e.stopPropagation();
                  setDetailOrder(order);
                }} title="Ver detalle completo" className="w-7 h-7 rounded-lg flex items-center justify-center text-white/20 hover:text-white/60 hover:bg-white/10 transition-colors" data-id="element-4747">
                        <Eye className="w-4 h-4" data-id="element-4748" />
                      </button>
                      <span className={cn('font-black tabular-nums tracking-tighter', isNatuCafeCocinaView ? 'text-3xl' : 'text-lg', waitMinutes >= 20 ? 'kds-late-timer' : waitMinutes >= 10 ? 'text-amber-500' : 'text-emerald-500')} data-id="element-4749">
                        {waitMinutes}m
                      </span>
                    </div>
                  </div>

                  {/* Customer / Mesa label.
                   NatuCafé (id 19) — solo en la vista Cocina la
                   línea se agranda enormemente para que el cocinero
                   identifique la mesa de lejos. Otras estaciones de
                   NatuCafé y otros restaurantes mantienen la línea
                   fina original. */}
                  <div className={cn('border-b border-white/5', isNatuCafeCocinaView ? 'px-3 py-2' : 'px-2.5 py-1')} data-id="element-4750">
                    <p className={cn('font-medium truncate', isNatuCafeCocinaView ? 'text-4xl font-black text-white leading-tight' : 'text-xs text-white/50')} data-id="element-4751">
                      {order.customer_name}
                    </p>
                  </div>

                  {/* Delivery Status Banner */}
                  {order.delivery_type === 'delivery' && deliveryJobsData[order.id] && <div className="border-b border-white/5" data-id="element-4752">
                        {order.status === 'searching_driver' && <div className="px-2.5 py-1.5 bg-orange-500/10 flex items-center gap-1.5" data-id="element-4753">
                            <Search className="w-3 h-3 text-orange-400 animate-pulse" data-id="element-4754" />
                            <p className="text-[10px] text-orange-400 font-bold leading-tight" data-id="element-4755">
                              Buscando conductor —{' '}
                              {deliveryJobsData[order.id].searchingMinutes}m
                            </p>
                          </div>}
                        {order.status === 'ready' && deliveryJobsData[order.id].driverName && <div className="px-2.5 py-1.5 bg-emerald-500/10 flex items-center gap-1.5" data-id="element-4756">
                              <Check className="w-3 h-3 text-emerald-400" data-id="element-4757" />
                              <p className="text-[10px] text-emerald-400 font-bold leading-tight truncate" data-id="element-4758">
                                Conductor asignado:{' '}
                                {deliveryJobsData[order.id].driverName} — en
                                camino al restaurante
                              </p>
                            </div>}
                        {order.status === 'out_for_delivery' && deliveryJobsData[order.id].driverName && <div className="px-2.5 py-1.5 bg-cyan-500/10 flex items-center gap-1.5" data-id="element-4759">
                              <Truck className="w-3 h-3 text-cyan-400" data-id="element-4760" />
                              <p className="text-[10px] text-cyan-400 font-bold leading-tight truncate" data-id="element-4761">
                                Conductor en camino:{' '}
                                {deliveryJobsData[order.id].driverName}
                              </p>
                            </div>}
                      </div>}

                  {/* Notes */}
                  {order.notes && <div className="px-2.5 py-1.5 bg-red-500/10 border-b border-red-500/20" data-id="element-4762">
                      <p className="text-[11px] text-red-400 font-bold leading-tight truncate" data-id="element-4763">
                        ⚠ {order.notes}
                      </p>
                    </div>}

                  {/* Items — full specs visible, no truncation */}
                  <div className="flex-1 overflow-y-auto max-h-[520px]" data-id="element-4764">
                    {visibleItems.map((item: any) => {
                const isItemReady = item.status === 'ready' || item.status === 'served';
                const isItemPreparing = item.status === 'preparing';
                const isUpdatingItem = updatingItems.has(item.id);
                return <div key={item.id} onClick={() => {
                  if (isUpdatingItem || isItemReady) return;
                  if (isNoiza) {
                    handleNoizaMarkReady(item.id);
                  } else {
                    handleMarkReady(item.id);
                  }
                }} className={cn('border-b border-white/5 transition-all',
                // NatuCafé Cocina: stack vertical compacto
                // — gap chico para que entren MUCHOS items
                // por tarjeta sin sacrificar legibilidad.
                isNatuCafeCocinaView ? 'flex flex-col gap-1.5 px-3 py-2.5' : 'flex items-center gap-2 px-2.5 py-1.5', isItemReady ? 'opacity-30 cursor-default' : isItemPreparing ? 'bg-amber-500/10 cursor-pointer select-none hover:bg-amber-500/20 active:scale-[0.98] border-l-2 border-l-amber-500' : 'cursor-pointer select-none hover:bg-white/10 active:scale-[0.98] border-l-2 border-l-white/20 hover:border-l-amber-400')} data-id="element-4765">
                          {/* Qty */}
                          <span className={cn('rounded-full flex items-center justify-center font-black shrink-0', isNatuCafeCocinaView ? 'w-10 h-10 text-xl' : 'w-6 h-6 text-xs', isItemReady ? 'bg-emerald-500/20 text-emerald-400' : isItemPreparing ? 'bg-amber-500 text-black' : 'bg-white/15 text-white')} data-id="element-4766">
                            {item.quantity}
                          </span>

                          {/* Name + options — full specs, wrap, no truncation */}
                          <div className="flex-1 min-w-0" data-id="element-4767">
                            <p className={cn('font-bold leading-snug break-words', isNatuCafeCocinaView ? 'text-2xl font-black' : 'text-base', isItemReady ? 'line-through text-white/40' : 'text-white')} data-id="element-4768">
                              {item.item_name_snapshot}
                            </p>
                            {item.options && item.options.length > 0 && <ul className={cn('mt-1', isNatuCafeCocinaView ? 'space-y-1' : 'space-y-0.5')} data-id="element-4769">
                                {item.options.map((o: any, idx: number) => <li key={idx} className={cn('text-white/80 leading-snug break-words flex gap-1.5', isNatuCafeCocinaView ? 'text-lg font-bold' : 'text-sm')} data-id="element-4770">
                                    <span className="text-white/30 shrink-0" data-id="element-4771">
                                      •
                                    </span>
                                    <span data-id="element-4772">{o.option_name}</span>
                                  </li>)}
                              </ul>}
                            {item.notes && <p className={cn('mt-1 font-black text-red-400 uppercase leading-snug break-words', isNatuCafeCocinaView ? 'text-lg' : 'text-xs')} data-id="element-4773">
                                ** {item.notes}
                              </p>}
                          </div>

                          {/* Status indicator */}
                          <div className="shrink-0 flex items-center gap-1" data-id="element-4774">
                            {isUpdatingItem ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" data-id="element-4775" /> : isItemReady ? <Check className={cn('text-emerald-500', isNatuCafeCocinaView ? 'w-7 h-7' : 'w-5 h-5')} data-id="element-4776" /> : <div className={cn('flex items-center gap-1 bg-emerald-500/20 rounded-md', isNatuCafeCocinaView ? 'px-3 py-1.5 gap-1.5' : 'px-2 py-1')} data-id="element-4777">
                                <Check className={cn('text-emerald-400', isNatuCafeCocinaView ? 'w-5 h-5' : 'w-4 h-4')} data-id="element-4778" />
                                <span className={cn('font-black text-emerald-400', isNatuCafeCocinaView ? 'text-base' : 'text-[10px]')} data-id="element-4779">
                                  LISTO
                                </span>
                              </div>}
                          </div>
                        </div>;
              })}
                  </div>

                  {/* Action — compact but tappable */}
                  <div className="p-2 border-t border-white/5" data-id="element-4780">
                    {/* Rancho San Francisco — botón manual de impresión
                     siempre visible. Manda al bridge para imprimir en
                     la impresora predeterminada. */}
                    {isRanchoSanFrancisco && <button onClick={() => handleManualPrint(order)} disabled={printingOrders.has(order.id)} className="w-full h-10 mb-2 rounded-lg text-sm font-black bg-white/10 hover:bg-white/20 text-white flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50 border border-white/10" data-id="element-4781">
                        {printingOrders.has(order.id) ? <>
                            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" data-id="element-4782" />
                            ENVIANDO...
                          </> : <>🖨 IMPRIMIR</>}
                      </button>}
                    {isGlobalRole && isGlobalStation(activeStation) ? <>
                        {order.status === 'new' && <button onClick={() => handleOrderAction(order.id, 'accept')} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4783">
                            {isUpdatingOrder ? '...' : '✓ ACEPTAR'}
                          </button>}
                        {order.status === 'accepted' && <button onClick={() => handleOrderAction(order.id, 'start_preparation')} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4784">
                            {isUpdatingOrder ? '...' : '🔥 COCINAR'}
                          </button>}
                        {order.status === 'preparing' && <button onClick={() => handleOrderAction(order.id, 'mark_ready')} disabled={isUpdatingOrder || !allItemsReady} className={cn('w-full h-10 rounded-lg text-sm font-black flex items-center justify-center gap-1 transition-all', allItemsReady ? 'bg-emerald-500 hover:bg-emerald-400 text-black active:scale-95' : isPartiallyReady ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20 cursor-not-allowed' : 'bg-white/5 text-white/20 cursor-not-allowed')} data-id="element-4785">
                            {isUpdatingOrder ? '...' : allItemsReady ? '✅ LISTO' : isPartiallyReady ? `FALTAN ${visibleItems.length - readyItemsCount} de ${visibleItems.length}` : `🍳 ${readyItemsCount}/${visibleItems.length} listos`}
                          </button>}
                        {(order.status === 'ready' || order.status === 'searching_driver') && order.delivery_type === 'delivery' && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4786">
                              {isUpdatingOrder ? '...' : <>
                                  <Truck className="w-4 h-4" data-id="element-4787" />
                                  MARCAR COMO ENVIADO
                                </>}
                            </button>}
                        {order.status === 'ready' && order.delivery_type !== 'delivery' && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4788">
                              {isUpdatingOrder ? '...' : '🚀 ENTREGAR'}
                            </button>}
                        {order.status === 'out_for_delivery' && <div className="h-10 flex items-center justify-center bg-cyan-500/10 border border-cyan-500/20 rounded-lg gap-2" data-id="element-4789">
                            <span className="text-cyan-400 font-black text-sm" data-id="element-4790">
                              🚗 Conductor en camino
                            </span>
                          </div>}
                        {order.status === 'delivered' && <div className="h-10 flex items-center justify-center bg-emerald-500/10 border border-emerald-500/20 rounded-lg gap-2" data-id="element-4791">
                            <span className="text-emerald-400 font-black text-sm" data-id="element-4792">
                              ✅ Entregado
                            </span>
                          </div>}
                        {['new', 'accepted', 'preparing'].includes(order.status) && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] text-white/15 hover:text-red-500 transition-colors mt-1" data-id="element-4793">
                            cancelar
                          </button>}
                      </> : <div className="space-y-2" data-id="element-4794">
                        {allItemsReady ? <div className={cn('flex items-center justify-center bg-emerald-500/10 border border-emerald-500/20 rounded-lg', isNatuCafeCocinaView ? 'h-20' : 'h-10')} data-id="element-4795">
                            <p className={cn('text-emerald-500 font-black', isNatuCafeCocinaView ? 'text-3xl' : 'text-sm')} data-id="element-4796">
                              ✅ TODOS LISTOS
                            </p>
                          </div> : <button onClick={() => handleStationAction(order.id, activeStation)} disabled={isUpdatingOrder} className={cn('w-full rounded-lg font-black bg-emerald-500 hover:bg-emerald-400 text-black flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50', isNatuCafeCocinaView ? 'h-20 text-3xl gap-3' : 'h-10 text-sm')} data-id="element-4797">
                            {isUpdatingOrder ? <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" data-id="element-4798" /> : <>
                                <Check className={cn(isNatuCafeCocinaView ? 'w-10 h-10' : 'w-5 h-5')} data-id="element-4799" />
                                MARCAR {visibleItems.length - readyItemsCount}{' '}
                                LISTO
                                {visibleItems.length - readyItemsCount !== 1 ? 'S' : ''}
                              </>}
                          </button>}
                      </div>}
                  </div>
                </div>;
        }) : <div className="col-span-full py-20 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-xl border border-white/5 border-dashed" data-id="element-4800">
              <ChefHatIcon className="w-16 h-16 text-white/10 mb-4" data-id="element-4801" />
              <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-4802">
                🎉 ¡Todo al día!
              </h3>
              <p className="text-sm" data-id="element-4803">Sin pedidos pendientes.</p>
            </div>}
        </div>

        {/* ═══ ORDER DETAIL MODAL ═══ */}
        {detailOrder && <KDSOrderDetailModal order={detailOrder} isOpen={!!detailOrder} onClose={() => setDetailOrder(null)} onAction={handleOrderAction} isUpdating={detailOrder ? updatingOrders.has(detailOrder.id) : false} data-id="element-4804" />}

        <VoidConfirmModal isOpen={voidModalState.isOpen} onClose={() => setVoidModalState({
        isOpen: false,
        orderId: null,
        originalAmount: 0
      })} onConfirm={handleVoidConfirm} title="Anular Pedido" description="Por favor, indica quién autoriza y el motivo de la anulación del pedido. Esta acción quedará registrada en el reporte de caja." data-id="element-4805" />

        <MenuToggleDrawer restaurantId={restaurant.id} isOpen={isMenuDrawerOpen} onClose={() => setIsMenuDrawerOpen(false)} data-id="element-4806" />

        {/* ═══ TOAST NOTIFICATIONS ═══ */}
        {toasts.length > 0 && <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none" data-id="element-4807">
            {toasts.map(toast => <div key={toast.id} className={cn('pointer-events-auto flex items-center gap-3 px-4 py-2.5 rounded-xl shadow-2xl border text-sm font-bold min-w-[240px] max-w-[360px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-4808">
                <span className="flex-1" data-id="element-4809">{toast.message}</span>
                <button onClick={() => removeToast(toast.id)} className="p-0.5 hover:bg-white/20 rounded transition-colors shrink-0" data-id="element-4810">
                  <X className="w-3.5 h-3.5" data-id="element-4811" />
                </button>
              </div>)}
          </div>}
      </div>
    </AdminLayout>;
}
function ChefHatIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4812">
      <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589c-.345 0-.682.041-1.005.115-.428-1.833-2.136-3.134-4.088-3.134s-3.66 1.301-4.088 3.134c-.323-.074-.66-.115-1.005-.115a4 4 0 0 0-2.134 7.589c.411.197.727.584.727 1.041V20a1 1 0 0 0 1 1h12z" data-id="element-4813" />
      <path d="M6 17h12" data-id="element-4814" />
    </svg>;
}